import { api } from '@/lib/api';
import { useTranslation } from '@/lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import useTextoOpcaoBioWeb from '../../-components/opcoes/bioweb/useTextoOpcaoBioWeb';
import type { ClienteSolicitacao } from '../../-components/opcoes/cliente';
import PaginaCliente, {
  type PaginaClienteOpcao,
} from '../../-components/pagina-cliente/pagina-cliente';
import { parseIdCliente } from '../../-utils/parametros-cliente';
import HibridoPageComponent from './-component';

export const Route = createFileRoute(
  '/__authenticated/comercial/produto/clientes/hibridos/$id',
)({
  params: {
    parse: params => ({ id: parseIdCliente(params.id) }),
    stringify: params => ({ id: String(params.id) }),
  },
  context: ({ params }) => ({
    hibridoQueryOptions: api.queryOptions(
      'get',
      '/clientes/hibridos/{id}',
      {
        params: { path: { id: params.id } },
      },
      { staleTime: 1000 * 60 },
    ),
    cliente: {
      tipoCliente: 'Hibrido',
      codigo: params.id,
    } satisfies ClienteSolicitacao as ClienteSolicitacao,
  }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(context.hibridoQueryOptions);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('private');

  const { hibridoQueryOptions, cliente } = Route.useRouteContext();
  const search = Route.useSearch();

  const { data: hibrido, isPending, error } = useQuery(hibridoQueryOptions);

  const textoBioWeb = useTextoOpcaoBioWeb(cliente);

  const cancelado = hibrido?.situacao === 'Cancelado';

  const opcoes: PaginaClienteOpcao[] = [
    {
      texto: t($ => $.clientes.paginaCliente.hibrido.backupsRemotos.titulo),
      to: '/comercial/produto/clientes/hibridos/$id/backups-remotos',
    },
  ];

  if (!cancelado) {
    opcoes.push({
      texto: t($ => $.clientes.opcoes.cancelarContrato.titulo),
      to: '/comercial/produto/clientes/hibridos/$id/cancelar-contrato',
    });
  }

  if (hibrido?.exibeLocaisHd) {
    opcoes.push({
      texto: t($ => $.clientes.paginaCliente.hibrido.locaisHd.titulo),
      to: '/comercial/produto/clientes/hibridos/$id/locais-hd',
    });
  }

  opcoes.push(
    {
      texto: t($ => $.clientes.opcoes.outrasSolicitacoes.titulo),
      to: '/comercial/produto/clientes/hibridos/$id/outras-solicitacoes',
    },
    {
      texto: t($ => $.clientes.opcoes.minhasSolicitacoes.titulo),
      to: '/comercial/produto/clientes/hibridos/$id/minhas-solicitacoes',
    },
    {
      texto: t($ => $.clientes.paginaCliente.hibrido.dadosSoftware),
      to: '/comercial/produto/clientes/hibridos/$id/dados-software',
    },
  );

  if (textoBioWeb) {
    opcoes.push({
      texto: textoBioWeb,
      to: '/comercial/produto/clientes/hibridos/$id/bioweb',
    });
  }

  return (
    <PaginaCliente
      search={search}
      carregando={isPending}
      erro={error}
      opcaoAberta={<Outlet />}
      opcoes={opcoes}
    >
      <HibridoPageComponent />
    </PaginaCliente>
  );
}
